import {
  addHours,
  addMinutes,
  fromUnixTime,
  getUnixTime,
  isAfter,
  isBefore,
  subHours,
} from 'date-fns';
import { Jobs } from '../collections/jobs';
import { Customer } from '../models/customer';
import { CreateEmploymentArgs, Employment } from '../models/employment';
import { Job } from '../models/job';
import { Transfer } from '../models/transfer';
import { getNewUnixTimestamp } from './removeTimeZoneOffset';

export type JobConditionalTransferTransferability = {
  canTransfer: boolean;
  blockedUntil?: number;
};

export type EmploymentConditionalTransferTransferability = {
  canTransfer: boolean;
  blockedUntil?: Date;
};

export function canTransferJobToCustomer(
  job: Job,
  transferTargetId: string,
  jobCustomer: Customer,
): JobConditionalTransferTransferability {
  if (
    !jobCustomer?.customerTransfers?.transferConditions?.jobTransferConditions
      ?.length ||
    !job.certificates?.length ||
    !job.transfers?.length
  ) {
    return { canTransfer: true };
  }
  const transferConditions =
    jobCustomer.customerTransfers.transferConditions.jobTransferConditions.filter(
      (condition) =>
        condition.certificates?.some((certificate) =>
          job.certificates?.some(
            (jobCertificate) => jobCertificate.id === certificate.id,
          ),
        ),
    );

  let transferConditionConditions = transferConditions
    .map((condition) => condition.conditions)
    ?.flat()
    ?.filter(
      (condition) =>
        !condition.hoursBeforeJobStart ||
        new Date() > subHours(job.jobStart, condition.hoursBeforeJobStart),
    );

  if (!transferConditionConditions) {
    return { canTransfer: true };
  }

  let transferCustomerGroups = transferConditionConditions
    .map((condition) => condition.transferCustomerGroups)
    ?.flat();

  let canTransfer = false;
  let blockedUntil = 0;
  let previousGroupNotTransferred = false;

  transferCustomerGroups.forEach((group) => {
    if (canTransfer) return;
    if (
      group.preferredTransferCustomers.some(
        (customer) => customer.id === transferTargetId,
      )
    ) {
      if (!blockedUntil && !previousGroupNotTransferred) {
        canTransfer = true;
      }
    } else {
      if (group.blockingHours) {
        let preferredTransfers = job.transfers!.filter((transfer) =>
          group.preferredTransferCustomers.some(
            (customer) => customer.id === transfer.targetCustomer.id,
          ),
        );

        preferredTransfers.forEach((preferredTransfer) => {
          if (previousGroupNotTransferred) return;
          if (!preferredTransfer.transferredAt) {
            previousGroupNotTransferred = true;
            blockedUntil = 0;
          } else {
            //   Use AddMinutes instead of AddHours here as AddHours ignores decimal values
            let currentBlockedUntilDate = addMinutes(
              fromUnixTime(preferredTransfer.transferredAt),
              (group.blockingHours || 0) * 60,
            );
            let currentBlockedUntil = getUnixTime(currentBlockedUntilDate);
            if (!blockedUntil || currentBlockedUntil < blockedUntil) {
              blockedUntil = currentBlockedUntil;
            }
          }
        });
      }
    }
  });
  if (blockedUntil && blockedUntil < getNewUnixTimestamp()) {
    canTransfer = true;
  }
  return {
    canTransfer,
    blockedUntil: blockedUntil ? blockedUntil : undefined,
  };
}

export function setTransferBlockedUntil(
  job: Job,
  customer: Customer,
  transfer: Transfer,
) {
  let conditionalTransfers = canTransferJobToCustomer(
    job,
    transfer.targetCustomer.id,
    customer,
  );

  if (transfer.auto || transfer.directTransfer) {
    if ((conditionalTransfers.blockedUntil || 0) > (transfer.transferAt || 0)) {
      Jobs.update(
        {
          _id: job._id,
          'transfers.targetCustomer.id': transfer.targetCustomer.id,
        },
        {
          $set: {
            'transfers.$.transferAt': conditionalTransfers.blockedUntil,
          },
        },
      );
    } else if (!conditionalTransfers.blockedUntil) {
      Jobs.update(
        {
          _id: job._id,
          'transfers.targetCustomer.id': transfer.targetCustomer.id,
        },
        {
          $set: {
            'transfers.$.auto': false,
            'transfers.$.directTransfer': false,
          },
        },
      );
    }
  }
}

export function canTransferEmploymentToCustomer(
  employment: Employment | CreateEmploymentArgs,
  transferTargetId: string,
  jobCustomer: Customer,
): EmploymentConditionalTransferTransferability {
  if (
    !jobCustomer?.customerTransfers?.transferConditions
      ?.employmentTransferConditions?.length ||
    !employment.certificates?.length ||
    !employment.transfers?.length
  ) {
    return { canTransfer: true };
  }

  const transferConditions =
    jobCustomer.customerTransfers.transferConditions.employmentTransferConditions.filter(
      (condition) =>
        condition.certificates?.some((certificate) =>
          employment.certificates?.some(
            (employmentCertificate) =>
              employmentCertificate.id === certificate.id,
          ),
        ),
    );

  let transferConditionConditions = transferConditions
    .map((condition) => condition.conditions)
    ?.flat()
    ?.filter(
      (condition) =>
        !condition.hoursBeforeJobStart ||
        new Date() >
          subHours(
            new Date(employment.startDate),
            condition.hoursBeforeJobStart,
          ),
    );

  if (!transferConditionConditions) {
    return { canTransfer: true };
  }

  let transferCustomerGroups = transferConditionConditions
    .map((condition) => condition.transferCustomerGroups)
    ?.flat();

  let blockedUntil: Date | undefined = undefined;
  let canTransfer = false;
  let previousGroupNotTransferred = false;
  transferCustomerGroups.forEach((group) => {
    if (canTransfer) return;
    if (
      group.preferredTransferCustomers.some(
        (customer) => customer.id === transferTargetId,
      )
    ) {
      if (!blockedUntil && !previousGroupNotTransferred) {
        canTransfer = true;
      }
    } else {
      if (group.blockingHours) {
        let preferredTransfers = employment.transfers!.filter((transfer) =>
          group.preferredTransferCustomers.some(
            (customer) => customer.id === transfer.targetCustomer.id,
          ),
        );

        preferredTransfers.forEach((preferredTransfer) => {
          if (previousGroupNotTransferred) return;
          if (!preferredTransfer.transferredAt) {
            previousGroupNotTransferred = true;
            blockedUntil = undefined;
          } else {
            let currentBlockedUntil = addHours(
              preferredTransfer.transferredAt,
              group.blockingHours || 0,
            );
            if (!blockedUntil || isBefore(currentBlockedUntil, blockedUntil)) {
              blockedUntil = currentBlockedUntil;
            }
          }
        });
      }
    }
  });
  if (blockedUntil && isAfter(new Date(), blockedUntil)) {
    canTransfer = true;
  }
  return {
    canTransfer,
    blockedUntil: blockedUntil ? blockedUntil : undefined,
  };
}
